import { useEffect, useMemo, useRef } from "react";
import Lottie from "lottie-react";
import { humanizeGemLabel } from "../constants/gems";
import { formatPotionAbsenceLabel, formatPotionCostLines } from "../constants/potions";
import { resolvePotionStarsAnimation } from "../utils/colorizeLottieAnimation";
import PotionBannerTitle from "./PotionBannerTitle";
import potionArabesqueSrc from "../assets/arabesquepotion.svg";

function buildGemImageUrl(gemId) {
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}/gems/${encodeURIComponent(`${gemId}.png`)}`;
}

export default function PotionCard({
  potion,
  quantity = 0,
  affinityGem,
  canObtain = false,
  onObtain,
  canUse = false,
  useLabel = "Utiliser",
  onUse,
  activeEffectLabel = "",
}) {
  const lottieRef = useRef(null);
  const starsAnimation = useMemo(
    () => resolvePotionStarsAnimation(potion),
    [potion]
  );

  const gemId = potion.cost.specificGemId;
  const gemLabel = affinityGem?.label ?? humanizeGemLabel(`${gemId}.png`);
  const gemImageUrl = affinityGem?.imageUrl ?? buildGemImageUrl(gemId);
  const gemCount = potion.cost.specificGemCount ?? 1;
  const gemTooltipId = `potion-gem-tooltip-${potion.id}`;
  const costLines = formatPotionCostLines(potion);

  useEffect(() => {
    lottieRef.current?.stop();

    const timeoutId = window.setTimeout(() => {
      lottieRef.current?.goToAndPlay(0, true);
    }, potion.starsAnimationDelay ?? 0);

    return () => window.clearTimeout(timeoutId);
  }, [potion.starsAnimationDelay, starsAnimation]);

  return (
    <article
      className={`potion-card ${potion.themeClass}${
        canObtain ? " potion-card--can-obtain" : ""
      }${activeEffectLabel ? " potion-card--has-active-effect" : ""}`}
    >
      <div className="potion-card__crest">
        <h3 className="potion-card__title">{potion.name}</h3>
        <PotionBannerTitle name={potion.name} />
        <div className="potion-card__visual">
          <div className="potion-card__icon-wrap">
            <div className="potion-card__lottie-wrap" aria-hidden="true">
              <Lottie
                lottieRef={lottieRef}
                animationData={starsAnimation}
                loop
                className="potion-card__lottie"
                rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
              />
            </div>
            <img
              src={potion.image}
              alt=""
              className="potion-card__image"
              decoding="sync"
              draggable={false}
            />
          </div>
        </div>
      </div>

      <div className="potion-card__description-wrap">
        {activeEffectLabel ? (
          <div className="potion-card__description-block">
            <p className="potion-card__description">{potion.description}</p>
            <p className="potion-card__active-effect">{activeEffectLabel}</p>
          </div>
        ) : (
          <p className="potion-card__description">{potion.description}</p>
        )}

        <img
          src={potionArabesqueSrc}
          alt=""
          className="potion-card__arabesque"
          draggable={false}
          aria-hidden="true"
        />

        <div className="potion-card__actions">
          <div className="potion-card__cost-block">
            <div className="potion-card__cost">
              <p className="potion-card__cost-line potion-card__cost-line--price">
                <span className="potion-card__cost-label">Prix&nbsp;:</span>
                <span
                  className="potion-card__price-gem"
                  aria-label={`${gemCount} ${gemLabel}`}
                >
                  <span className="potion-card__price-count">{gemCount}</span>
                  <span className="potion-card__gem-wrap">
                    <img
                      src={gemImageUrl}
                      alt=""
                      className="potion-card__gem-icon"
                      decoding="sync"
                      draggable={false}
                      tabIndex={0}
                      aria-describedby={gemTooltipId}
                    />
                    <span id={gemTooltipId} className="potion-card__gem-tooltip" role="tooltip">
                      {gemLabel}
                    </span>
                  </span>
                </span>
              </p>
              <p className="potion-card__cost-line potion-card__cost-line--secondary">
                {costLines.commonPart}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="potion-card__obtain"
            disabled={!canObtain}
            onClick={() => onObtain?.(potion)}
          >
            Obtenir
          </button>
          {typeof onUse === "function" && !activeEffectLabel ? (
            <button
              type="button"
              className="potion-card__obtain potion-card__obtain--use"
              disabled={!canUse}
              onClick={() => onUse(potion)}
            >
              {useLabel}
            </button>
          ) : null}
        </div>
      </div>

      <div className="potion-card__quantity">
        {quantity > 0 ? (
          <>
            <span className="potion-card__quantity-label">Vous en possédez</span>
            <strong className="potion-card__quantity-value">{quantity}</strong>
          </>
        ) : (
          <span className="potion-card__quantity-label">
            {formatPotionAbsenceLabel(potion)}
          </span>
        )}
      </div>
    </article>
  );
}
